import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const ResidentialLanding = () => {
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pageview',
      page: '/residential',
      audience: 'Residential'
    });
  }, []);

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>URGAA for Residential Users - Reliable EV Charging Network Access | Go4Garage</title>
        <meta name="title" content="URGAA for Residential Users - Reliable EV Charging Network" />
        <meta name="description" content="Find guaranteed-uptime charging stations near you. Single app for 1,000+ stations across India. Smart charging recommendations, transparent pricing, and 98% uptime SLA." />
        <meta name="keywords" content="EV charging app India, electric vehicle charging, EV charger near me, EV charging stations India, electric car charging app, residential EV charging" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urgaa.in/residential" />
        <meta property="og:title" content="URGAA - Reliable EV Charging Network" />
        <meta property="og:description" content="Find guaranteed-uptime charging stations near you. 1,000+ stations across India." />
        <meta property="og:image" content="https://urgaa.in/images/og-residential.jpg" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://urgaa.in/residential" />
        <meta name="twitter:title" content="URGAA - Reliable EV Charging" />
        <meta name="twitter:description" content="Find guaranteed-uptime charging stations across India" />
        <meta name="twitter:image" content="https://urgaa.in/images/twitter-residential.jpg" />
        <meta name="twitter:site" content="@Go4Garage" />
        
        {/* Additional Meta */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://urgaa.in/residential" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            "name": "URGAA",
            "operatingSystem": "iOS, Android",
            "applicationCategory": "TransportationApplication",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.6",
              "ratingCount": "12500"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "INR"
            },
            "description": "Find reliable EV charging stations across India with guaranteed uptime",
            "provider": {
              "@type": "Organization",
              "name": "Go4Garage Private Limited",
              "url": "https://urgaa.in"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-block px-4 py-2 bg-teal-100 text-teal-800 rounded-full text-sm font-semibold mb-6">
              🚗 12,500+ Users | 4.6⭐ Rating | 1,000+ Stations
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Never Worry About<br />Finding a Working Charger
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              India's first EV charging network with guaranteed 98% uptime. Real-time availability, smart recommendations, transparent pricing.
            </p>
            <div className="flex gap-4 justify-center">
              <a href="#download" className="px-8 py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-semibold">
                Download App
              </a>
              <Link to="/login" className="px-8 py-4 border-2 border-teal-600 text-teal-600 rounded-lg hover:bg-teal-50 font-semibold">
                Sign Up
              </Link>
            </div>
            <p className="text-sm text-gray-500 mt-4">Free to download | Pay only for charging | Works nationwide</p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-16 bg-red-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Common EV Charging Frustrations</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-red-600 text-4xl mb-4">🚫</div>
                <h3 className="text-xl font-bold mb-2">"Is it Working?"</h3>
                <p className="text-gray-600">40% of chargers are broken when you reach. Apps show availability but reality is different.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-red-600 text-4xl mb-4">📱</div>
                <h3 className="text-xl font-bold mb-2">"Too Many Apps"</h3>
                <p className="text-gray-600">Need 5-6 different apps for different charging networks. Confusing pricing across operators.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-red-600 text-4xl mb-4">⏱️</div>
                <h3 className="text-xl font-bold mb-2">"Planning Nightmare"</h3>
                <p className="text-gray-600">Long trips are stressful. Which route has working chargers? Will I get stuck?</p>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">How URGAA Solves This</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border-2 border-teal-500 p-6 rounded-lg">
                <div className="text-teal-600 text-4xl mb-4">✅</div>
                <h3 className="text-xl font-bold mb-2">98% Uptime Guarantee</h3>
                <p className="text-gray-600 mb-4">Real-time monitoring ensures chargers actually work when the app says they do</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Live status updated every 60 seconds</li>
                  <li>✓ If charger fails, get ₹500 credit</li>
                  <li>✓ Automated testing every 4 hours</li>
                  <li>✓ Alternative charger recommendations if down</li>
                </ul>
              </div>
              <div className="border-2 border-blue-500 p-6 rounded-lg">
                <div className="text-blue-600 text-4xl mb-4">🗺️</div>
                <h3 className="text-xl font-bold mb-2">Smart Routing for Long Trips</h3>
                <p className="text-gray-600 mb-4">AI-powered trip planner with charging stops and backup options</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Enter destination, we plan charging stops</li>
                  <li>✓ Accounts for your battery level & model</li>
                  <li>✓ Backup chargers along route</li>
                  <li>✓ Reserve chargers 15 minutes in advance</li>
                </ul>
              </div>
              <div className="border-2 border-green-500 p-6 rounded-lg">
                <div className="text-green-600 text-4xl mb-4">💰</div>
                <h3 className="text-xl font-bold mb-2">Transparent Pricing</h3>
                <p className="text-gray-600 mb-4">No surprises. Clear pricing before you start charging.</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ See ₹/kWh rate before plugging in</li>
                  <li>✓ Estimate total cost for your charge</li>
                  <li>✓ No hidden fees or platform charges</li>
                  <li>✓ Monthly pass options (₹499-999)</li>
                </ul>
              </div>
              <div className="border-2 border-purple-500 p-6 rounded-lg">
                <div className="text-purple-600 text-4xl mb-4">📱</div>
                <h3 className="text-xl font-bold mb-2">Single App for All Networks</h3>
                <p className="text-gray-600 mb-4">1,000+ stations from 50+ CPOs in one app</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Works with all major CPOs (Tata, Reliance, etc.)</li>
                  <li>✓ One wallet, one account</li>
                  <li>✓ Unified billing & receipts</li>
                  <li>✓ Compare prices across nearby stations</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* App Features */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Features You'll Love</h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl mb-2">🔔</div>
                <h4 className="font-bold text-sm mb-1">Charge Complete Alerts</h4>
                <p className="text-xs text-gray-600">Get notified when charging is done</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl mb-2">📊</div>
                <h4 className="font-bold text-sm mb-1">Usage Analytics</h4>
                <p className="text-xs text-gray-600">Track your kWh, costs, CO₂ saved</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl mb-2">🏫</div>
                <h4 className="font-bold text-sm mb-1">Home Charging Logs</h4>
                <p className="text-xs text-gray-600">Track home & public charging together</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl mb-2">🎯</div>
                <h4 className="font-bold text-sm mb-1">Favorites & History</h4>
                <p className="text-xs text-gray-600">Quick access to your go-to chargers</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl mb-2">🌟</div>
                <h4 className="font-bold text-sm mb-1">Loyalty Rewards</h4>
                <p className="text-xs text-gray-600">Earn points, get discounts</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl mb-2">💬</div>
                <h4 className="font-bold text-sm mb-1">In-App Support</h4>
                <p className="text-xs text-gray-600">Chat support if anything goes wrong</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl mb-2">🛡️</div>
                <h4 className="font-bold text-sm mb-1">Safety Ratings</h4>
                <p className="text-xs text-gray-600">User reviews & safety scores</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl mb-2">🔋</div>
                <h4 className="font-bold text-sm mb-1">Offline Mode</h4>
                <p className="text-xs text-gray-600">Download maps for no-network areas</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-8">Simple, Pay-As-You-Go Pricing</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-teal-600 mb-3">Pay Per Charge</h3>
                <div className="text-4xl font-bold mb-2">₹20-40</div>
                <div className="text-sm text-gray-600 mb-4">per kWh (varies by location)</div>
                <ul className="text-left text-sm text-gray-600 space-y-2 mb-4">
                  <li>✓ No monthly fees</li>
                  <li>✓ Pay only for what you use</li>
                  <li>✓ UPI, cards, wallets accepted</li>
                  <li>✓ Best for: Occasional charging</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg border-2 border-teal-500">
                <div className="inline-block px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-semibold mb-2">POPULAR</div>
                <h3 className="text-2xl font-bold text-teal-600 mb-3">Monthly Pass</h3>
                <div className="text-4xl font-bold mb-2">₹499</div>
                <div className="text-sm text-gray-600 mb-4">per month (save 20%)</div>
                <ul className="text-left text-sm text-gray-600 space-y-2 mb-4">
                  <li>✓ 20% discount on all charges</li>
                  <li>✓ Priority access at busy stations</li>
                  <li>✓ Free route planning (unlimited)</li>
                  <li>✓ Best for: Regular commuters</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Download Section */}
        <section id="download" className="py-16 bg-gradient-to-br from-teal-600 to-blue-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-4">Download URGAA Today</h2>
            <p className="text-xl mb-8">Join 12,500+ EV drivers already charging worry-free</p>
            <div className="flex gap-4 justify-center mb-6">
              <a href="#" className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-900 font-semibold flex items-center gap-2">
                <span></span> Download on App Store
              </a>
              <a href="#" className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-900 font-semibold flex items-center gap-2">
                <span>▶</span> Get it on Google Play
              </a>
            </div>
            <p className="text-sm opacity-90">Free to download | ₹100 welcome bonus | 4.6⭐ rating</p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <details className="bg-white p-4 rounded-lg shadow">
                <summary className="font-bold cursor-pointer">Which EV models are supported?</summary>
                <p className="text-sm text-gray-600 mt-2">All EVs with CCS2, CHAdeMO, Type 2 (AC), or Bharat DC connectors. Works with Tata Nexon EV, MG ZS EV, Hyundai Kona, all electric 2W/3W, etc.</p>
              </details>
              <details className="bg-white p-4 rounded-lg shadow">
                <summary className="font-bold cursor-pointer">What if the charger doesn't work?</summary>
                <p className="text-sm text-gray-600 mt-2">You get ₹500 credit automatically if a charger marked 'Available' doesn't work. Plus we'll recommend nearby alternatives.</p>
              </details>
              <details className="bg-white p-4 rounded-lg shadow">
                <summary className="font-bold cursor-pointer">Can I use this for long road trips?</summary>
                <p className="text-sm text-gray-600 mt-2">Yes! Our trip planner suggests charging stops with guaranteed uptime. We cover all major highways with charging stations every 60-80 km.</p>
              </details>
              <details className="bg-white p-4 rounded-lg shadow">
                <summary className="font-bold cursor-pointer">How do I pay for charging?</summary>
                <p className="text-sm text-gray-600 mt-2">Add money to your URGAA wallet via UPI, cards, or net banking. Charging cost is auto-deducted when session ends. Get instant receipt.</p>
              </details>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ResidentialLanding;